import { InfoIcon } from '@phosphor-icons/react'
import { toZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'
import z from 'zod'

import { getPersonColumn, PersonColumnSettings } from '@/components/business/person-column'
import { Badge } from '@/components/ui/badge'
import { BadgeColorKeys } from '@/components/ui/badge/badge.types'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { TableColumn } from '@/components/ui/table'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants'
import {
  MarketShopOrderStatus,
  MarketShopOrderStatusEnum,
  MarketShopTransactionModel,
} from '@/models'
import { formatDateOnly, formatPrice, formatTimeOnly } from '@/utils'

import * as S from './market-shop-transactions.styled'
import { getMarketShopResident } from './market-shop-transactions.utils'

export type MarketShopTransactionStatusFilter = MarketShopOrderStatus | 'all'

export const getMarketShopStatusLabelsMap = (
  t: TFunction<['market-shop-dashboard']>,
): Record<MarketShopOrderStatus, string> => ({
  awaiting_pickup: t('market-shop-dashboard:transactions.status.awaitingPickup'),
  completed: t('market-shop-dashboard:transactions.status.completed'),
  declined: t('market-shop-dashboard:transactions.status.declined'),
  in_progress: t('market-shop-dashboard:transactions.status.inProgress'),
  new: t('market-shop-dashboard:transactions.status.new'),
})

export const MARKET_SHOP_STATUS_COLORS_MAP: Record<MarketShopOrderStatus, BadgeColorKeys> = {
  awaiting_pickup: 'green',
  completed: 'neutral',
  declined: 'red',
  in_progress: 'blue',
  new: 'orange',
}

export const getMarketShopTransactionsColumns = ({
  isRestaurantMode,
  residentColumnSettings,
  t,
  timezone,
}: {
  isRestaurantMode: boolean
  residentColumnSettings: PersonColumnSettings
  t: TFunction<['market-shop-dashboard']>
  timezone: string
}): TableColumn<MarketShopTransactionModel>[] => {
  const statusLabels = getMarketShopStatusLabelsMap(t)

  return [
    {
      id: 'orderId',
      render: (transaction) => transaction.orderId,
      title: t('market-shop-dashboard:transactions.columns.orderId'),
      width: '14rem',
    },
    // Note: the "Resident" person column of EL-15064 / EL-15927 (BE key MarketShop → resident).
    // Only restaurant orders are placed by residents - a plain shop has no one to show.
    ...(isRestaurantMode
      ? [
          getPersonColumn<MarketShopTransactionModel>(residentColumnSettings, {
            getDisplay: (row) => row.residentDisplay,
            getPerson: getMarketShopResident,
            id: 'residentName',
            pageName: t('market-shop-dashboard:transactions.pageName'),
            source: 'market-shop',
            title: t('market-shop-dashboard:transactions.columns.residentName'),
          }),
        ]
      : []),
    {
      id: 'order',
      render: (transaction) => transaction.orderSummary,
      title: t('market-shop-dashboard:transactions.columns.order'),
      width: '50rem',
    },
    {
      id: 'orderPlaced',
      render: (transaction) =>
        transaction.completedAt ? (
          <S.DateTimeCell>
            <Typography as="span" color="black600" size="text-small" weight="medium">
              {formatDateOnly(toZonedTime(transaction.completedAt, timezone))}
            </Typography>
            <Typography as="span" color="black600" size="text-small" weight="medium">
              {formatTimeOnly(toZonedTime(transaction.completedAt, timezone))}
            </Typography>
          </S.DateTimeCell>
        ) : (
          EMPTY_VALUE
        ),
      sortKey: 'completedAt',
      title: t('market-shop-dashboard:transactions.columns.orderPlaced'),
      width: '17rem',
    },
    {
      id: 'orderStatus',
      render: (transaction) => (
        <Badge
          color={MARKET_SHOP_STATUS_COLORS_MAP[transaction.orderStatus]}
          variant="light"
          size="small"
        >
          {statusLabels[transaction.orderStatus]}
        </Badge>
      ),
      title: t('market-shop-dashboard:transactions.columns.orderStatus'),
      width: '16.4rem',
    },
    ...(isRestaurantMode
      ? [
          {
            id: 'deliveryType',
            render: (transaction: MarketShopTransactionModel) => {
              if (!transaction.deliveryType) return '-'
              if (transaction.deliveryType === 'delivery') {
                return (
                  <Badge color="orange" variant="light" size="small">
                    {t('market-shop-dashboard:transactions.deliveryType.delivery')}
                  </Badge>
                )
              }
              return (
                <Badge color="neutral" variant="light" size="small">
                  {t('market-shop-dashboard:transactions.deliveryType.pickup')}
                </Badge>
              )
            },
            title: t('market-shop-dashboard:transactions.columns.deliveryType'),
            width: '18rem',
          },
        ]
      : []),
    {
      id: 'transactionId',
      render: (transaction) => transaction.transactionId,
      title: t('market-shop-dashboard:transactions.columns.transactionId'),
      width: '25rem',
    },
    {
      id: 'total',
      render: (transaction) => {
        const discount = transaction.discount || 0
        const subTotal = transaction.total - transaction.tax + discount

        return (
          <S.TotalCell>
            <Typography as="span" color="black600" size="text-small" weight="medium">
              {formatPrice(transaction.total, { zeroPrice: true })}
            </Typography>
            {!!transaction.total && (
              <Tooltip triggerIcon={InfoIcon}>
                <div>
                  <div>
                    {t('market-shop-dashboard:transactions.totalTooltip.subTotal', {
                      amount: formatPrice(subTotal, { zeroPrice: true }),
                    })}
                  </div>
                  {discount > 0 && (
                    <div>
                      {t('market-shop-dashboard:transactions.totalTooltip.discount', {
                        amount: formatPrice(discount, { zeroPrice: true }),
                      })}
                    </div>
                  )}
                  <div>
                    {t('market-shop-dashboard:transactions.totalTooltip.tax', {
                      amount: formatPrice(transaction.tax, { zeroPrice: true }),
                    })}
                  </div>
                  <div>
                    {t('market-shop-dashboard:transactions.totalTooltip.paymentMethod', {
                      method: transaction.paymentMethod,
                    })}
                  </div>
                </div>
              </Tooltip>
            )}
          </S.TotalCell>
        )
      },
      title: t('market-shop-dashboard:transactions.columns.total'),
      width: '15rem',
    },
  ]
}

export const marketShopTransactionsQueryFiltersSchema = z.object({
  fromDate: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
  order: z.enum(['asc', 'desc']).optional(),
  search: z.coerce.string().optional(),
  skip: z.coerce.number().optional(),
  sort: z.coerce.string().optional(),
  status: z.enum([...MarketShopOrderStatusEnum.options, 'all']).optional(),
  toDate: z.coerce.number().optional(),
})

export const marketShopTransactionsInitialFilters: z.infer<
  typeof marketShopTransactionsQueryFiltersSchema
> = {
  fromDate: undefined,
  limit: DEFAULT_LIMIT,
  order: 'desc',
  search: '',
  skip: 0,
  sort: 'completedAt',
  status: undefined,
  toDate: undefined,
}
